import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { AdElement, Animation } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';

import { CommandRegistry } from '../../canvas/commandRegistry';
import { commitDragGesture } from '../../canvas/dragCommands';
import {
  beginDrag,
  cancelDrag,
  previewDragPosition,
  updateDragFromScreenDelta,
  type DragDraft,
} from '../../canvas/gesture';
import {
  applyDocumentLayoutToAdElements,
  documentFromEditorElements,
} from '../../canvas/layoutBridge';
import {
  combinedAxisAlignedBounds,
  normalizeRect,
  pickTargetsInRect,
  pickTopmostAtPoint,
  type AxisAlignedBounds,
} from '../../canvas/selection';
import {
  clampZoom,
  fitFrameToViewport,
  MAX_ZOOM,
  MIN_ZOOM,
  panBy,
  screenToStage,
  stepZoom,
  viewportAfterStageSizeChange,
  zoomAtViewportCenter,
  type Point,
  type ViewportState,
} from '../../canvas/viewport';
import { toHitTargets } from './toHitTargets';

/** Ignore keyboard shortcuts when we are on an editable element. E.g: typing in a text input. */
export function shouldIgnoreCanvasShortcut(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const contentEditable = target.getAttribute('contenteditable');

  if (target.isContentEditable || contentEditable === '' || contentEditable === 'true') {
    return true;
  }

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function shouldHandleSpacePan(args: {
  alreadyArmed: boolean;
  pointerOverStage: boolean;
  stage: HTMLElement | null;
  target: EventTarget | null;
}): boolean {
  if (args.alreadyArmed || args.pointerOverStage) {
    return true;
  }
  return Boolean(args.stage && args.target instanceof Node && args.stage.contains(args.target));
}

export type UseEditorCanvasSessionArgs = {
  elements: AdElement[];
  resolvedElements: AdElement[];
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  animation?: Animation | null;
  frame: { width: number; height: number };
  stageSize: { width: number; height: number };
  selectedElementIds: string[];
  onSelectionChange: (elementIds: string[]) => void;
  onElementsChange: (elements: AdElement[]) => void;
  clipToFrame?: boolean;
};

type MarqueeState = {
  originStage: Point;
  currentStage: Point;
  /** Selection to merge into on release when the marquee is additive. */
  baseSelection: string[];
  additive: boolean;
};

function sameIds(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/** Screen px the pointer must travel before a press turns into a drag, so click jitter moves nothing. */
const DRAG_START_THRESHOLD_PX = 4;

function isAdditiveModifier(event: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }) {
  return event.shiftKey || event.metaKey || event.ctrlKey;
}

export type EditorCanvasSession = {
  stageRef: React.RefObject<HTMLDivElement>;
  viewport: ViewportState;
  displayArtwork: AdElement[];
  selectionBounds: ReturnType<typeof combinedAxisAlignedBounds>;
  /** One entry per visible selected element; empty unless more than one is selected. */
  selectedItemBounds: Array<{ id: string; bounds: AxisAlignedBounds }>;
  /** Live marquee rectangle in stage coordinates, or null when no marquee is being drawn. */
  marqueeBounds: AxisAlignedBounds | null;
  hoverBounds: ReturnType<typeof combinedAxisAlignedBounds>;
  isHoveringTarget: boolean;
  isPanArmed: boolean;
  isPanning: boolean;
  undoDepth: number;
  redoDepth: number;
  clipToFrame: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetViewport: () => void;
  panByScreen: (delta: { x: number; y: number }) => void;
  undo: () => void;
  redo: () => void;
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: (event: ReactPointerEvent) => void;
  onPointerLeave: () => void;
};

export function useEditorCanvasSession({
  elements,
  resolvedElements,
  animation = null,
  frame,
  stageSize,
  selectedElementIds,
  onSelectionChange,
  onElementsChange,
  clipToFrame = false,
}: UseEditorCanvasSessionArgs): EditorCanvasSession {
  const stageRef = useRef<HTMLDivElement>(null);
  const registryRef = useRef(new CommandRegistry());
  const draftRef = useRef<DragDraft | null>(null);
  const marqueeRef = useRef<MarqueeState | null>(null);
  /** Where the current element press started; `engaged` flips once the threshold is crossed. */
  const dragOriginRef = useRef<{ x: number; y: number; engaged: boolean } | null>(null);
  /** Set when pressing an already-selected element: a click without movement collapses to it. */
  const collapseToIdRef = useRef<string | null>(null);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const pointerSessionRef = useRef<{
    pointerId: number;
    lastClientX: number;
    lastClientY: number;
  } | null>(null);

  const [viewport, setViewport] = useState<ViewportState>(() =>
    stageSize.width > 0 && stageSize.height > 0
      ? fitFrameToViewport(frame, stageSize, 24)
      : { panX: 0, panY: 0, zoom: 1 },
  );
  const [draft, setDraft] = useState<DragDraft | null>(null);
  const [marquee, setMarquee] = useState<MarqueeState | null>(null);
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);
  const [undoDepth, setUndoDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);
  const [isPanArmed, setIsPanArmed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const spaceHeldRef = useRef(false);
  const pointerOverStageRef = useRef(false);

  const viewportPadding = 24;
  const frameWidth = frame.width;
  const frameHeight = frame.height;
  const stageWidth = stageSize.width;
  const stageHeight = stageSize.height;

  const layoutRef = useRef({
    frameWidth,
    frameHeight,
    stageWidth,
    stageHeight,
  });

  useEffect(() => {
    const previous = layoutRef.current;
    const frameChanged = previous.frameWidth !== frameWidth || previous.frameHeight !== frameHeight;
    const stageChanged = previous.stageWidth !== stageWidth || previous.stageHeight !== stageHeight;
    layoutRef.current = { frameWidth, frameHeight, stageWidth, stageHeight };

    const frame = { width: frameWidth, height: frameHeight };
    const stage = { width: stageWidth, height: stageHeight };

    if (frameChanged) {
      if (stage.width > 0 && stage.height > 0) {
        setViewport(fitFrameToViewport(frame, stage, viewportPadding));
      }
      return;
    }

    if (stageChanged) {
      setViewport((current) => {
        const next = viewportAfterStageSizeChange(
          { width: previous.stageWidth, height: previous.stageHeight },
          stage,
          frame,
          current.zoom,
          viewportPadding,
        );
        return next ?? current;
      });
    }
  }, [frameWidth, frameHeight, stageWidth, stageHeight, viewportPadding]);

  const document = useMemo(
    () => documentFromEditorElements(elements, animation ?? null),
    [animation, elements],
  );

  const displayArtwork = useMemo(() => {
    const previewDocument = { ...document, elements: [...document.elements] };
    if (draft && !draft.cancelled) {
      for (const elementId of draft.elementIds) {
        const preview = previewDragPosition(draft, elementId);
        const layout = previewDocument.elements.find((entry) => entry.id === elementId);
        if (preview && layout) {
          layout.x = preview.x;
          layout.y = preview.y;
        }
      }
    }
    return applyDocumentLayoutToAdElements(resolvedElements, previewDocument).filter(
      (element) => element.hidden !== true,
    );
  }, [document, draft, resolvedElements]);

  const hitTargets = useMemo(() => toHitTargets(displayArtwork), [displayArtwork]);

  const syncRegistry = useCallback((registry: CommandRegistry) => {
    setUndoDepth(registry.undoDepth);
    setRedoDepth(registry.redoDepth);
  }, []);

  const beginPointerSession = useCallback((event: ReactPointerEvent) => {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Cypress and other test runners may synthesize pointer events without an active pointer.
    }
    pointerSessionRef.current = {
      pointerId: event.pointerId,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
    };
  }, []);

  const endPointerSession = useCallback((event: ReactPointerEvent) => {
    pointerSessionRef.current = null;
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore release failures when capture was never established.
    }
  }, []);

  const clientDeltaSinceLastMove = useCallback((event: ReactPointerEvent) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) {
      return null;
    }
    const delta = {
      x: event.clientX - session.lastClientX,
      y: event.clientY - session.lastClientY,
    };
    pointerSessionRef.current = {
      ...session,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
    };
    return delta;
  }, []);

  const stagePointFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) {
        return { x: 0, y: 0 };
      }
      const screen = { x: clientX - rect.left, y: clientY - rect.top };
      return screenToStage(screen, viewport);
    },
    [viewport],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (event.button !== 0) {
        return;
      }
      if (spaceHeldRef.current) {
        event.preventDefault();
        panStartRef.current = {
          x: event.clientX,
          y: event.clientY,
          panX: viewport.panX,
          panY: viewport.panY,
        };
        setIsPanning(true);
        beginPointerSession(event);
        return;
      }

      const stagePoint = stagePointFromEvent(event.clientX, event.clientY);
      const picked = pickTopmostAtPoint(hitTargets, stagePoint, { includeLocked: true });
      const additive = isAdditiveModifier(event);
      collapseToIdRef.current = null;

      if (!picked) {
        // Empty space: start a marquee. Selection (including clearing) is applied on release.
        const baseSelection = additive ? selectedElementIds : [];
        const nextMarquee: MarqueeState = {
          originStage: stagePoint,
          currentStage: stagePoint,
          baseSelection,
          additive,
        };
        marqueeRef.current = nextMarquee;
        setMarquee(nextMarquee);
        beginPointerSession(event);
        return;
      }

      const pickedTarget = hitTargets.find((target) => target.id === picked);
      const wasSelected = selectedElementIds.includes(picked);

      let nextSelection: string[];
      if (additive) {
        nextSelection = wasSelected
          ? selectedElementIds.filter((id) => id !== picked)
          : [...selectedElementIds, picked];
      } else if (wasSelected && selectedElementIds.length > 1 && !pickedTarget?.locked) {
        // Keep the group so it can be dragged together; collapse on a click without movement.
        nextSelection = selectedElementIds;
        collapseToIdRef.current = picked;
      } else {
        nextSelection = [picked];
      }

      if (!sameIds(nextSelection, selectedElementIds)) {
        onSelectionChange(nextSelection);
      }

      // A locked element can be selected but never starts a drag, and a deselect never drags.
      if (pickedTarget?.locked || !nextSelection.includes(picked)) {
        collapseToIdRef.current = null;
        return;
      }

      // Locked members stay where they are. Hidden members still move so the group keeps its
      // relative layout, which is why this reads from `elements` rather than the hit targets.
      const draggableIds = nextSelection.filter((id) => {
        const element = elements.find((entry) => entry.id === id);
        return element != null && element.locked !== true;
      });
      const startPositions: Record<string, { x: number; y: number }> = {};
      const movableIds: string[] = [];
      for (const id of draggableIds) {
        const layout = document.elements.find((entry) => entry.id === id);
        if (layout) {
          startPositions[id] = { x: layout.x, y: layout.y };
          movableIds.push(id);
        }
      }

      const nextDraft = beginDrag(movableIds, startPositions);
      draftRef.current = nextDraft;
      dragOriginRef.current = { x: event.clientX, y: event.clientY, engaged: false };
      setDraft(nextDraft);
      beginPointerSession(event);
    },
    [
      beginPointerSession,
      document.elements,
      elements,
      hitTargets,
      onSelectionChange,
      selectedElementIds,
      stagePointFromEvent,
      viewport.panX,
      viewport.panY,
    ],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      if (panStartRef.current) {
        const delta = {
          x: event.clientX - panStartRef.current.x,
          y: event.clientY - panStartRef.current.y,
        };
        setViewport({
          panX: panStartRef.current.panX + delta.x,
          panY: panStartRef.current.panY + delta.y,
          zoom: viewport.zoom,
        });
        setHoveredElementId(null);
        return;
      }

      if (marqueeRef.current) {
        const current = marqueeRef.current;
        const next: MarqueeState = {
          ...current,
          currentStage: stagePointFromEvent(event.clientX, event.clientY),
        };
        marqueeRef.current = next;
        setMarquee(next);

        // Selection is applied on release so the sidebar doesn't resize the viewport mid-drag.
        setHoveredElementId(null);
        return;
      }

      if (draftRef.current && !draftRef.current.cancelled) {
        const origin = dragOriginRef.current;
        if (origin && !origin.engaged) {
          const travelled = Math.hypot(event.clientX - origin.x, event.clientY - origin.y);
          if (travelled < DRAG_START_THRESHOLD_PX) {
            setHoveredElementId(null);
            return;
          }
          // The session still sits at the press point, so the first delta carries the full travel.
          origin.engaged = true;
        }
        const delta = clientDeltaSinceLastMove(event);
        if (delta && (delta.x !== 0 || delta.y !== 0)) {
          // Advance the ref synchronously. Doing it inside a setState updater defers it to render
          // time, where it could overwrite an Escape cancel that landed in between.
          const current = draftRef.current;
          if (current && !current.cancelled) {
            const updated = updateDragFromScreenDelta(current, delta, viewport.zoom);
            draftRef.current = updated;
            setDraft(updated);
          }
        }
        setHoveredElementId(null);
        return;
      }

      const stagePoint = stagePointFromEvent(event.clientX, event.clientY);
      const picked = pickTopmostAtPoint(hitTargets, stagePoint, { includeLocked: true });
      setHoveredElementId(picked);
    },
    [clientDeltaSinceLastMove, hitTargets, stagePointFromEvent, viewport.zoom],
  );

  const onPointerLeave = useCallback(() => {
    pointerOverStageRef.current = false;
    setHoveredElementId(null);
  }, []);

  const finishPointer = useCallback(
    (event: ReactPointerEvent) => {
      panStartRef.current = null;
      setIsPanning(false);
      endPointerSession(event);

      const activeDraft = draftRef.current;
      if (activeDraft && !activeDraft.cancelled) {
        const committed = commitDragGesture(registryRef.current, document, activeDraft);
        if (committed) {
          onElementsChange(applyDocumentLayoutToAdElements(elements, committed.document));
          syncRegistry(committed.registry);
          registryRef.current = committed.registry;
        } else if (collapseToIdRef.current) {
          // Pressed a member of a multi-selection and released without moving.
          onSelectionChange([collapseToIdRef.current]);
        }
      }
      collapseToIdRef.current = null;
      dragOriginRef.current = null;
      draftRef.current = null;
      setDraft(null);
      const finishedMarquee = marqueeRef.current;
      if (finishedMarquee) {
        const hits = pickTargetsInRect(
          hitTargets,
          normalizeRect(finishedMarquee.originStage, finishedMarquee.currentStage),
        );
        const finalSelection = finishedMarquee.additive
          ? [
              ...finishedMarquee.baseSelection,
              ...hits.filter((id) => !finishedMarquee.baseSelection.includes(id)),
            ]
          : hits;
        if (!sameIds(finalSelection, selectedElementIds)) {
          onSelectionChange(finalSelection);
        }
      }
      marqueeRef.current = null;
      setMarquee(null);
    },
    [
      document,
      elements,
      endPointerSession,
      hitTargets,
      onElementsChange,
      onSelectionChange,
      selectedElementIds,
      syncRegistry,
    ],
  );

  const stageSizeForZoom = useCallback(
    () => ({
      width: stageRef.current?.clientWidth || stageSize.width,
      height: stageRef.current?.clientHeight || stageSize.height,
    }),
    [stageSize.height, stageSize.width],
  );

  const resetViewport = useCallback(() => {
    setViewport(fitFrameToViewport(frame, stageSize, viewportPadding));
  }, [frame, stageSize, viewportPadding]);

  const setZoom = useCallback(
    (zoom: number) => {
      const nextZoom = clampZoom(zoom);
      setViewport((current) => {
        if (current.zoom === nextZoom) {
          return current;
        }
        return zoomAtViewportCenter(current, stageSizeForZoom(), nextZoom);
      });
    },
    [stageSizeForZoom],
  );

  const zoomIn = useCallback(() => {
    setViewport((current) => {
      const nextZoom = stepZoom(current.zoom, 1);
      if (nextZoom === current.zoom) {
        return current;
      }
      return zoomAtViewportCenter(current, stageSizeForZoom(), nextZoom);
    });
  }, [stageSizeForZoom]);

  const zoomOut = useCallback(() => {
    setViewport((current) => {
      const nextZoom = stepZoom(current.zoom, -1);
      if (nextZoom === current.zoom) {
        return current;
      }
      return zoomAtViewportCenter(current, stageSizeForZoom(), nextZoom);
    });
  }, [stageSizeForZoom]);

  const panByScreen = useCallback((delta: { x: number; y: number }) => {
    setViewport((current) => panBy(current, delta));
  }, []);

  useEffect(() => {
    const armPan = () => {
      spaceHeldRef.current = true;
      setIsPanArmed(true);
    };
    const disarmPan = () => {
      spaceHeldRef.current = false;
      setIsPanArmed(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && draftRef.current) {
        const cancelled = cancelDrag(draftRef.current);
        draftRef.current = cancelled;
        collapseToIdRef.current = null;
        setDraft(cancelled);
        pointerSessionRef.current = null;
        panStartRef.current = null;
        setIsPanning(false);
      }
      if (event.key === 'Escape' && marqueeRef.current) {
        // Selection hasn't changed during the drag, so cancelling just drops the marquee.
        marqueeRef.current = null;
        setMarquee(null);
        pointerSessionRef.current = null;
      }

      if (shouldIgnoreCanvasShortcut(event.target)) {
        return;
      }

      if (event.key === ' ' || event.code === 'Space') {
        if (event.target instanceof HTMLElement && event.target.closest('button')) {
          return;
        }
        if (
          !shouldHandleSpacePan({
            alreadyArmed: spaceHeldRef.current,
            pointerOverStage: pointerOverStageRef.current,
            stage: stageRef.current,
            target: event.target,
          })
        ) {
          return;
        }
        event.preventDefault();
        armPan();
        return;
      }

      const meta = event.metaKey || event.ctrlKey;
      if (meta && (event.key === '+' || event.key === '=' || event.code === 'Equal')) {
        event.preventDefault();
        zoomIn();
        return;
      }
      if (meta && (event.key === '-' || event.key === '_' || event.code === 'Minus')) {
        event.preventDefault();
        zoomOut();
        return;
      }
      if (meta && (event.key === '0' || event.code === 'Digit0')) {
        event.preventDefault();
        resetViewport();
        return;
      }

      if (meta && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          const doc = documentFromEditorElements(elements, animation ?? null);
          const redone = registryRef.current.redo(doc);
          if (redone) {
            syncRegistry(registryRef.current);
            onElementsChange(applyDocumentLayoutToAdElements(elements, redone));
          }
          return;
        }
        const doc = documentFromEditorElements(elements, animation ?? null);
        const undone = registryRef.current.undo(doc);
        if (undone) {
          syncRegistry(registryRef.current);
          onElementsChange(applyDocumentLayoutToAdElements(elements, undone));
        }
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === ' ' || event.code === 'Space') {
        disarmPan();
      }
    };

    const onBlur = () => {
      disarmPan();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [animation, elements, onElementsChange, resetViewport, syncRegistry, zoomIn, zoomOut]);

  useEffect(() => {
    const node = stageRef.current;
    if (!node) {
      return undefined;
    }
    const onPointerEnter = () => {
      pointerOverStageRef.current = true;
    };
    const onPointerLeave = () => {
      pointerOverStageRef.current = false;
    };
    // Bound to the stage node so page scroll outside the canvas is unchanged.
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      event.preventDefault();
      panByScreen({ x: -event.deltaX, y: -event.deltaY });
    };
    node.addEventListener('pointerenter', onPointerEnter);
    node.addEventListener('pointerleave', onPointerLeave);
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      node.removeEventListener('pointerenter', onPointerEnter);
      node.removeEventListener('pointerleave', onPointerLeave);
      node.removeEventListener('wheel', onWheel);
    };
  }, [panByScreen, stageWidth, stageHeight]);

  const undo = useCallback(() => {
    const doc = documentFromEditorElements(elements, animation ?? null);
    const undone = registryRef.current.undo(doc);
    if (undone) {
      syncRegistry(registryRef.current);
      onElementsChange(applyDocumentLayoutToAdElements(elements, undone));
    }
  }, [animation, elements, onElementsChange, syncRegistry]);

  const redo = useCallback(() => {
    const doc = documentFromEditorElements(elements, animation ?? null);
    const redone = registryRef.current.redo(doc);
    if (redone) {
      syncRegistry(registryRef.current);
      onElementsChange(applyDocumentLayoutToAdElements(elements, redone));
    }
  }, [animation, elements, onElementsChange, syncRegistry]);

  const selectionBounds = combinedAxisAlignedBounds(hitTargets, selectedElementIds);
  const selectedItemBounds =
    selectedElementIds.length > 1
      ? hitTargets
          .filter((target) => selectedElementIds.includes(target.id))
          .map((target) => ({ id: target.id, bounds: target.bounds }))
      : [];
  const hoverBounds =
    hoveredElementId && !selectedElementIds.includes(hoveredElementId)
      ? combinedAxisAlignedBounds(hitTargets, [hoveredElementId])
      : null;
  const marqueeRect = marquee ? normalizeRect(marquee.originStage, marquee.currentStage) : null;
  const marqueeBounds =
    marqueeRect && (marqueeRect.width > 0 || marqueeRect.height > 0) ? marqueeRect : null;

  return {
    stageRef,
    viewport,
    displayArtwork,
    selectionBounds,
    selectedItemBounds,
    marqueeBounds,
    hoverBounds,
    isHoveringTarget: hoveredElementId != null,
    isPanArmed,
    isPanning,
    undoDepth,
    redoDepth,
    clipToFrame,
    canZoomIn: viewport.zoom < MAX_ZOOM,
    canZoomOut: viewport.zoom > MIN_ZOOM,
    setZoom,
    zoomIn,
    zoomOut,
    resetViewport,
    panByScreen,
    undo,
    redo,
    onPointerDown,
    onPointerMove,
    onPointerUp: finishPointer,
    onPointerCancel: finishPointer,
    onPointerLeave,
  };
}
